// One item's upkeep in Inventory (inventory prototype's item card; gap #15): its minimum and maximum
// at each location (stock.setLevels), which the morning picklists and the weekly reorder work from,
// and its primary vendor and their part number (stock.setItem), which the weekly reorder buys from.

import { useEffect, useRef, useState } from 'react';
import { client } from '../api.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { SaveButton } from '../shared/SaveButton.tsx';
import { boxText, qtyText, validQty, type Level, type StockLocation } from './model.ts';
import { levelProblem, type Vendor } from './orders-model.ts';
import '../review/review.css';
import './inventory.css';

export function ItemSheet({
  itemId,
  itemName,
  locations,
  mayManage,
  mayVendors,
  onClose,
  onChanged,
}: {
  itemId: string;
  itemName: string;
  locations: StockLocation[];
  mayManage: boolean;
  /** vendors.list needs purchasing.order. */
  mayVendors: boolean;
  onClose: () => void;
  onChanged: (said: string) => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  const levels = useQuery<{ levels: Level[] }>('stock.levels', { itemId });
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const at = (id: string) => levels.data?.levels.find((l) => l.locationId === id);
  return (
    <dialog ref={ref} className="sheet ask item-sheet" aria-labelledby="item-title" onClose={onClose}>
      <div className="row between">
        <h3 id="item-title">{itemName}</h3>
        <button
          type="button"
          className="btn quiet icon"
          aria-label="Close"
          onClick={() => ref.current?.close()}
        >
          ×
        </button>
      </div>
      <section aria-labelledby="item-levels">
        <h4 id="item-levels">Minimum and maximum</h4>
        <p className="muted small">
          Below its minimum, a truck refills to its maximum on the morning picklist, and the warehouse is
          reordered up to its maximum each week.
        </p>
        {levels.error && <p className="error">{levels.error}</p>}
        <ul className="level-rows">
          {locations.map((l) => (
            <LevelRow
              // a saved level re-reads: the row starts again from it
              key={`${l.id}:${at(l.id)?.min ?? ''}:${at(l.id)?.max ?? ''}`}
              location={l}
              level={at(l.id)}
              itemId={itemId}
              mayManage={mayManage}
              onSaved={(min, max) => {
                void levels.reload();
                onChanged(`${itemName} at ${l.name}: min ${min}, max ${max}.`);
              }}
            />
          ))}
        </ul>
      </section>
      {mayManage && mayVendors && (
        <VendorRow
          itemId={itemId}
          onSaved={(name) =>
            onChanged(
              name ? `${itemName} is reordered from ${name}.` : `${itemName} has no primary vendor now.`,
            )
          }
        />
      )}
      <div className="row end">
        <button type="button" className="btn soft" onClick={() => ref.current?.close()}>
          Close
        </button>
      </div>
    </dialog>
  );
}

function LevelRow({
  location,
  level,
  itemId,
  mayManage,
  onSaved,
}: {
  location: StockLocation;
  level: Level | undefined;
  itemId: string;
  mayManage: boolean;
  onSaved: (min: string, max: string) => void;
}) {
  const [min, setMin] = useState(level ? qtyText(level.min) : '');
  const [max, setMax] = useState(level ? qtyText(level.max) : '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const changed = !level || qtyText(level.min) !== min.trim() || qtyText(level.max) !== max.trim();
  const problem = min.trim() || max.trim() ? levelProblem(min, max) : null;
  const save = async () => {
    const why = levelProblem(min, max);
    if (why) return setError(why);
    setBusy(true);
    setError(null);
    try {
      await client.call('stock.setLevels', {
        locationId: location.id,
        itemId,
        min: min.trim(),
        max: max.trim(),
      });
      onSaved(min.trim(), max.trim());
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li className="level-row">
      <div>
        <b>{location.name}</b>
        <span className="muted small block">
          {level ? `${qtyText(level.onHand)} on hand` : 'Not stocked here yet'}
        </span>
      </div>
      {mayManage ? (
        <>
          <label>
            <span className="flabel">Min</span>
            <input
              className="field"
              inputMode="decimal"
              value={min}
              onChange={(e) => setMin(e.target.value)}
            />
          </label>
          <label>
            <span className="flabel">Max</span>
            <input
              className="field"
              inputMode="decimal"
              value={max}
              onChange={(e) => setMax(e.target.value)}
            />
          </label>
          <SaveButton
            busy={busy}
            busyLabel="Saving…"
            error={error ?? problem}
            disabled={!changed || !min.trim() || !max.trim() || !!problem}
            className="btn"
            onClick={() => void save()}
          >
            Save
          </SaveButton>
        </>
      ) : (
        <span>{level ? `min ${qtyText(level.min)} · max ${qtyText(level.max)}` : '–'}</span>
      )}
    </li>
  );
}

function VendorRow({ itemId, onSaved }: { itemId: string; onSaved: (vendorName: string | null) => void }) {
  const vendors = useQuery<{ vendors: Vendor[] }>('vendors.list', {});
  // What the item has now (pricebook.getItem, QA-376): the sheet opens on it, not on "No primary vendor".
  const item = useQuery<{
    primaryVendorId: string | null;
    primaryVendorName: string | null;
    vendorSku: string | null;
    purchaseUnit: string | null;
    unitsPerPurchase: string;
    tracked: boolean;
  }>('pricebook.getItem', { id: itemId });
  const [vendorId, setVendorId] = useState<string | null>(null);
  const [sku, setSku] = useState<string | null>(null);
  // how the vendor sells it (truck-stock parity 4): new PO lines are in that unit
  const [unit, setUnit] = useState<string | null>(null);
  const [per, setPer] = useState<string | null>(null);
  const [tracked, setTracked] = useState<boolean | null>(null);
  const vendorValue = vendorId ?? item.data?.primaryVendorId ?? '';
  const skuValue = sku ?? item.data?.vendorSku ?? '';
  const unitValue = unit ?? item.data?.purchaseUnit ?? '';
  const perValue = per ?? (item.data ? qtyText(item.data.unitsPerPurchase) : '1');
  const trackedValue = tracked ?? item.data?.tracked ?? true;
  const perOk = validQty(perValue) && Number(perValue) > 0;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      if (!perOk) return setError('How many it holds is a number above zero, like 10 or 2.5.');
      await client.call('stock.setItem', {
        itemId,
        primaryVendorId: vendorValue || null,
        vendorSku: skuValue.trim() || null,
        purchaseUnit: unitValue.trim() || null,
        unitsPerPurchase: perValue.trim(),
        tracked: trackedValue,
      });
      const name = vendors.data?.vendors.find((v) => v.id === vendorValue)?.name ?? null;
      setSaved(name ?? 'none');
      onSaved(name);
    } catch (e) {
      setError(errorText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <section aria-labelledby="item-vendor" className="item-vendor">
      <h4 id="item-vendor">Primary vendor</h4>
      <p className="muted small">
        The weekly reorder buys this item from them.{' '}
        {saved
          ? `Now: ${saved === 'none' ? 'no primary vendor' : saved}.`
          : item.data
            ? `Now: ${item.data.primaryVendorName ?? 'no primary vendor'}.`
            : ''}
      </p>
      <div className="fgrid">
        <label>
          <span className="flabel">Vendor</span>
          <select className="field" value={vendorValue} onChange={(e) => setVendorId(e.target.value)}>
            <option value="">No primary vendor</option>
            {(vendors.data?.vendors ?? [])
              .filter((v) => v.active)
              .map((v) => (
                <option key={v.id} value={v.id}>
                  {v.name}
                </option>
              ))}
          </select>
        </label>
        <label>
          <span className="flabel">Their part number (optional)</span>
          <input
            className="field"
            maxLength={100}
            value={skuValue}
            onChange={(e) => setSku(e.target.value)}
          />
        </label>
        <label>
          <span className="flabel">They sell it by the (optional)</span>
          <input
            className="field"
            maxLength={30}
            placeholder="box, bag, roll…"
            value={unitValue}
            onChange={(e) => setUnit(e.target.value)}
          />
        </label>
        <label>
          <span className="flabel">
            {unitValue.trim() ? `A ${unitValue.trim()} holds` : 'Each one holds'}
          </span>
          <input
            className="field num"
            inputMode="decimal"
            value={perValue}
            onChange={(e) => setPer(e.target.value)}
          />
        </label>
        <label className="checkrow">
          <input type="checkbox" checked={trackedValue} onChange={(e) => setTracked(e.target.checked)} /> Kept
          in stock and counted (off: pipe by the foot, a bag of fittings, used on jobs at its cost)
        </label>
      </div>
      {unitValue.trim() && perOk && (
        <p className="muted small">
          Ordered and received by the {unitValue.trim()} ({boxText('2', perValue, unitValue.trim())}); kept
          and counted one by one.
        </p>
      )}
      <div className="row-actions">
        <SaveButton busy={busy} busyLabel="Saving…" error={error} className="btn" onClick={() => void save()}>
          Set the vendor
        </SaveButton>
      </div>
    </section>
  );
}
